import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Tag, CheckCircle2, Package, ArrowLeft, Calendar, Sparkles, Users, MapPin, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { levelStyle } from "@/lib/constants";
import ReservationDialog from "@/components/ReservationDialog";

export default function ActivityDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/workshops/${id}`).then((r) => setW(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">Atelier introuvable</h1>
        <Link to="/nos-activites" className="inline-block mt-6 text-sky-600 font-bold">Retour aux activités</Link>
      </div>
    );

  if (!w)
    return <div className="max-w-7xl mx-auto px-4 py-24"><div className="h-96 bg-slate-100 rounded-3xl animate-pulse" /></div>;

  const s = levelStyle(w.level);
  const highlights = w.highlights?.length ? w.highlights : w.objectives || [];

  return (
    <div data-testid="activity-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/nos-activites" className="inline-flex items-center gap-2 text-slate-500 hover:text-sky-600 font-semibold text-sm transition-colors" data-testid="back-to-activities">
          <ArrowLeft className="w-4 h-4" /> Retour aux activités
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-2 gap-12">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="relative">
          <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-sky-500/20">
            <img src={w.image} alt={w.title} className="w-full h-[420px] object-cover" />
          </div>
          <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold ${s.bg} ${s.text}`}>
            <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
          </span>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Atelier · {w.competence}</span>
          <h1 className="font-heading font-black text-4xl lg:text-5xl text-slate-900 mt-2">{w.title}</h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{w.summary}</p>

          <div className="grid grid-cols-2 gap-3 mt-8">
            <InfoRow icon={GraduationCap} label="Public" value={`${s.label}`} />
            <InfoRow icon={Clock} label="Durée" value={w.duration} />
            <InfoRow icon={MapPin} label="Format" value="En classe, en présentiel" />
            <InfoRow icon={Users} label="Effectif" value="Classe entière" />
            <InfoRow icon={Tag} label="Tarif" value={`À partir de ${w.price.toFixed(0)} € / classe`} />
          </div>

          <div className="mt-8">
            <ReservationDialog
              kind="atelier"
              itemId={w.id}
              itemTitle={w.title}
              trigger={
                <button data-testid="reserve-workshop-button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-sky-500 text-white font-bold text-lg hover:bg-sky-600 hover:scale-[1.02] transition-[transform,background-color]">
                  <Calendar className="w-5 h-5" /> Réserver cet atelier
                </button>
              }
            />
          </div>
        </motion.div>
      </section>

      {/* Bienvenue / description longue */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 mb-4">À propos de cet atelier</h2>
        {w.intro && <p className="text-lg text-slate-700 leading-relaxed">{w.intro}</p>}
        <p className="text-slate-600 leading-relaxed mt-4">{w.description}</p>
      </section>

      {/* Points forts */}
      {highlights.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-[2rem] p-8 sm:p-10 text-white">
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl mb-6 flex items-center gap-2">
              <Sparkles className="w-6 h-6" /> Points forts
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {highlights.map((h) => (
                <div key={h} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-sky-100" />
                  <span className="text-sky-50">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Objectifs + Matériel */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-2 gap-6">
        <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h2 className="font-heading font-extrabold text-xl text-slate-900 flex items-center gap-2 mb-6">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Objectifs pédagogiques
          </h2>
          <ul className="space-y-3">
            {w.objectives?.map((o) => (
              <li key={o} className="flex items-start gap-3 text-slate-600 text-sm">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 shrink-0" /> {o}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h2 className="font-heading font-extrabold text-xl text-slate-900 flex items-center gap-2 mb-6">
            <Package className="w-5 h-5 text-amber-500" /> Matériel fourni
          </h2>
          <div className="flex flex-wrap gap-2">
            {w.materials?.map((m) => (
              <span key={m} className="px-4 py-2 rounded-full bg-amber-50 text-amber-700 text-sm font-semibold">{m}</span>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-6">Tout le matériel est apporté par l'animateur. Aucun prérequis technique pour l'école.</p>
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-slate-900 rounded-[2rem] p-8 sm:p-12 text-center">
          <h2 className="font-heading font-black text-3xl text-white">Envie de réserver « {w.title} » ?</h2>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">Choisissez une date parmi les disponibilités de nos animateurs. Nous vous recontactons pour confirmer.</p>
          <div className="mt-8 flex justify-center">
            <ReservationDialog
              kind="atelier"
              itemId={w.id}
              itemTitle={w.title}
              trigger={
                <button data-testid="reserve-workshop-cta" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-sky-500 text-white font-bold text-lg hover:bg-sky-400 hover:scale-[1.02] transition-[transform,background-color]">
                  <Calendar className="w-5 h-5" /> Réserver cet atelier
                </button>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-sky-100 text-sky-600 shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 font-semibold">{label}</p>
        <p className="font-heading font-extrabold text-slate-900 text-sm truncate">{value}</p>
      </div>
    </div>
  );
}
